import { Link, useParams } from "react-router"
import { motion } from "motion/react"

import { ProtectedRoute } from "@/lib/auth/protected-route"
import { AppLayout } from "@/components/layout/app-layout"
import { ALL_ROLES } from "@/lib/team/roles"
import { useOrganization } from "@/lib/organization/organization-context"
import { useGhlQueue } from "@/lib/api/use-ghl"
import { cx } from "@/lib/utils/cx"
import { Today } from "@/components/marketing/supercharge/today"
import { Contacts } from "@/components/marketing/supercharge/contacts"
import { ContactRecord } from "@/components/marketing/supercharge/contact-record"
import { Conversations } from "@/components/marketing/supercharge/conversations"
import { Pipeline } from "@/components/marketing/supercharge/pipeline"
import { Analytics } from "@/components/marketing/supercharge/analytics"

// Marketing Supercharge — the shop's CRM on its messaging platform. Today
// is the work; Contacts, Conversations and Pipeline are the book; the
// numbers come last. Everything under this sidebar item; nothing else in
// the app changes. Data: the mock's GoHighLevel-shaped store.

export type SuperchargeSection = "today" | "contacts" | "contact" | "conversations" | "pipeline" | "analytics"

export default function MarketingPage({ section }: { section: SuperchargeSection }) {
  const { activeTeamId } = useOrganization()
  const { contactId } = useParams<{ contactId: string }>()
  const { data: queue } = useGhlQueue(activeTeamId)
  const active = section === "contact" ? "contacts" : section

  const tabs: Array<{ id: Exclude<SuperchargeSection, "contact">; label: string; to: string; count?: number }> = [
    { id: "today", label: "Today", to: "/marketing", count: queue?.total },
    { id: "contacts", label: "Contacts", to: "/marketing/contacts" },
    { id: "conversations", label: "Conversations", to: "/marketing/conversations", count: queue?.groups.find((g) => g.key === "waiting")?.items.length },
    { id: "pipeline", label: "Pipeline", to: "/marketing/pipeline" },
    { id: "analytics", label: "Analytics", to: "/marketing/analytics" },
  ]

  return (
    <ProtectedRoute allowedRoles={ALL_ROLES}>
      <AppLayout>
        <div className="min-h-screen bg-zinc-50/40">
          <div className="mx-auto max-w-[1440px] px-4 py-5 md:px-6 md:py-6">
            <div className="flex flex-col gap-2 border-b border-zinc-200 md:flex-row md:items-end md:justify-between">
              <div className="min-w-0 pb-3">
                <h1 className="text-[22px] font-semibold tracking-tight text-zinc-900">Marketing Supercharge</h1>
                <p className="mt-0.5 text-[13px] text-zinc-500">The work, the people, the deals — one place.</p>
              </div>
              <nav role="tablist" aria-label="Marketing Supercharge" className="-mb-px flex gap-5 overflow-x-auto">
                {tabs.map((t) => {
                  const on = t.id === active
                  return (
                    <Link
                      key={t.id}
                      to={t.to}
                      role="tab"
                      aria-selected={on}
                      className={cx(
                        "relative h-11 shrink-0 whitespace-nowrap rounded-sm text-[13.5px] font-semibold transition-colors duration-150",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/30",
                        on ? "text-zinc-900" : "text-zinc-500 hover:text-zinc-800",
                      )}
                    >
                      <span className="inline-flex h-11 items-center gap-1.5">
                        {t.label}
                        {t.count != null && t.count > 0 && (
                          <span className={cx("inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-bold tabular-nums", on ? "bg-zinc-900 text-white" : "bg-zinc-200 text-zinc-700")}>{t.count}</span>
                        )}
                      </span>
                      {on && (
                        <motion.span
                          layoutId="supercharge-tab"
                          className="absolute inset-x-0 bottom-0 h-0.5 bg-zinc-900"
                          transition={{ type: "spring", stiffness: 480, damping: 38 }}
                        />
                      )}
                    </Link>
                  )
                })}
              </nav>
            </div>

            <div className="mt-5">
              {activeTeamId == null ? (
                <p className="py-16 text-center text-[13.5px] text-zinc-500">Pick a store to see its Marketing Supercharge.</p>
              ) : section === "today" ? (
                <Today teamId={activeTeamId} />
              ) : section === "contacts" ? (
                <Contacts teamId={activeTeamId} />
              ) : section === "contact" ? (
                <ContactRecord teamId={activeTeamId} contactId={contactId ?? ""} />
              ) : section === "conversations" ? (
                <Conversations teamId={activeTeamId} />
              ) : section === "pipeline" ? (
                <Pipeline teamId={activeTeamId} />
              ) : (
                <Analytics teamId={activeTeamId} />
              )}
            </div>
          </div>
        </div>
      </AppLayout>
    </ProtectedRoute>
  )
}
